import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetGlobalSurgicalPeriodRulesetByIdQuery,
  useUpdateGlobalSurgicalPeriodRulesetMutation,
} from "store/queries/globalSurgicalPeriodRulesets";

import {
  IGlobalSurgicalPeriodFormData,
  INITIAL_GLOBAL_SURGICAL_PERIOD_FORM_DATA,
  GlobalSurgicalPeriodForm,
} from "./components/GlobalSurgicalPeriodForm";

export function EditGlobalSurgicalPeriodPage() {
  useDocumentTitle("Edit Global Surgical Period Rule");
  const navigate = useNavigate();
  const { id } = useParams();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } =
    useGetGlobalSurgicalPeriodRulesetByIdQuery(id ?? "");
  const [updateMutation, { isLoading: isUpdating }] =
    useUpdateGlobalSurgicalPeriodRulesetMutation();
  const [formData, setFormData] = useState<IGlobalSurgicalPeriodFormData>(
    INITIAL_GLOBAL_SURGICAL_PERIOD_FORM_DATA
  );

  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    setFormData({
      groupName: ruleset.groupName ?? "",
      providerId: ruleset.providerId,
      payerId: ruleset.payerId,
      applicability: ruleset.applicability,
      lookbackDays: ruleset.lookbackDays,
      requireGlobalRemark: ruleset.requireGlobalRemark,
      allowPostopMap: ruleset.allowPostopMap,
      contractLanguage: ruleset.contractLanguage ?? "",
    });
  }, [data]);

  const handleSubmit = async () => {
    if (!id) return;
    if (formData.applicability === "SCOPED" && !formData.providerId) return;
    setIsLoading(true);
    try {
      await updateMutation({
        id,
        groupName: formData.groupName.trim() || null,
        // CLE-2524: immutable — the locked form value matches the stored row.
        applicability: formData.applicability,
        providerId:
          formData.applicability === "SCOPED" ? formData.providerId : null,
        payerId: formData.applicability === "TYPICAL" ? null : formData.payerId,
        lookbackDays: formData.lookbackDays,
        requireGlobalRemark: formData.requireGlobalRemark,
        allowPostopMap: formData.allowPostopMap,
        contractLanguage: formData.contractLanguage.trim() || null,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Updated!",
        detail: "Global surgical period rule updated successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message ||
          "Failed to update global surgical period rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <>
      <GlobalSurgicalPeriodForm
        title="Edit Global Surgical Period Rule"
        submitLabel="Save Changes"
        formData={formData}
        onChange={setFormData}
        onSubmit={handleSubmit}
        isSubmitting={isUpdating}
        scopeLocked
      />
      <RuleHistory histories={data?.data.history} />
    </>
  );
}
